import React, { ReactNode, useEffect, useMemo, useState, useCallback } from "react";

// UX
import CardImagingOrderInterfaceUX from "./CardImagingOrderInterfaceUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Utils
import { Checkbox } from "semantic-ui-react";
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SaveType,
  FilterSearchType,
  WidthsType,
  GenerateTableHeader,
  PaymentStatusOptions,
  OrderStatusOptions,
  OrderTypeOptions,
  OrderItemsHeader,
} from "./sequence/ImagingOrderInterface";

// Serializer
import ImagingOrderInterfaceSerializer from "issara-sdk/types/ImagingOrderSerializer_apps_IME";
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";

// Type
type CardImagingOrderInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  ImagingOrderInterfaceSequence: State["ImagingOrderInterfaceSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardImagingOrderInterface = (props: CardImagingOrderInterfaceProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedImagingOrder, setSelectedImagingOrder] = useState<any[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "ImagingOrderInterface", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data ?? ""}</div>;
  };

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      let newData = (props.ImagingOrderInterfaceSequence?.ImagingOrderInterfaceList || []).map(
        (item: ImagingOrderInterfaceSerializer) => item.id
      );

      setSelectedImagingOrder(newData);
    } else {
      setSelectedImagingOrder([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "ImagingOrderInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      page: 0,
    });

    setSelectedImagingOrder([]);
  };

  const handleSave = (type: SaveType) => {
    props.runSequence({
      sequence: "ImagingOrderInterface",
      action: "SAVE",
      selectedItems: selectedImagingOrder,
      cardKey: CARD_NAME,
      type,
    });

    setSelectedImagingOrder([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "ImagingOrderInterface",
      action: "CLEAR",
      cardKey: CARD_NAME,
    });

    setSelectedImagingOrder([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;
    props.setProp(`ImagingOrderInterfaceSequence.searchImagingOrder.${name}`, data?.value);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`ImagingOrderInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "ImagingOrderInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      [key]: value,
    });

    setSelectedImagingOrder([]);
    props.setProp(`ImagingOrderInterfaceSequence.selectedItems`, []);
  };

  const handleDisabledTrigger = useMemo(() => {
    if (selectedImagingOrder.length === 0) return true;
  }, [selectedImagingOrder, props.ImagingOrderInterfaceSequence?.ImagingOrderInterfaceList]);

  const formatEmr = useCallback(
    (encounterData: EncounterSerializer | undefined, emrId: number | undefined | null) => {
      const id = encounterData?.id ? encounterData.id : "-";
      const number = encounterData?.number ? encounterData?.number : "-";
      const patient = encounterData?.patient_name;

      return `[EMR ${emrId}] [${id} : ${number}] ${patient}`;
    },
    [props.ImagingOrderInterfaceSequence?.encounterList]
  );

  // useMemo
  const imagingOrderInterfaceList = useMemo(() => {
    return (props.ImagingOrderInterfaceSequence?.ImagingOrderInterfaceList || [])
    .map(convertToRow)
    .map(
      (item: ImagingOrderInterfaceSerializer & any) => {
        const paymentStatus = (PaymentStatusOptions || []).find(
          (data: any) => data?.key === item.order_payment_status
        );

        const orderStatus = (OrderStatusOptions || []).find(
          (data: any) => data?.key === item.order_status
        );

        const orderType = (OrderTypeOptions || []).find(
          (data: any) => data?.value === item.order_type
        );

        const encounterData = (props.ImagingOrderInterfaceSequence?.encounterList || []).find(
          (data: EncounterSerializer) => data?.id === item.encounter
        );

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={selectedImagingOrder.includes(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  setSelectedImagingOrder([...selectedImagingOrder, item.id]);
                } else {
                  let newData = selectedImagingOrder.filter((key: any) => key !== item.id);
                  setSelectedImagingOrder(newData);
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: any, header) => {
              const key: any = header.accessor;

              if (key === "order_payment_status") {
                data[key] = columnTableCenter(paymentStatus?.text);
              } else if (key === "order_status") {
                data[key] = columnTableCenter(orderStatus?.text);
              } else if (key === "order_type") {
                data[key] = columnTableCenter(orderType?.text);
              } else if (divisionColumns.includes(key)) {
                columnTableCenter(
                  (data[key] = (props.masterOptions?.division || []).find(
                    (division: any) => division?.value === item[key]
                  )?.text)
                );
              } else if (doctorColumns.includes(key)) {
                columnTableCenter(
                  (data[key] = (props.ImagingOrderInterfaceSequence?.doctorOptions || []).find(
                    (doctor: any) => doctor?.id === item[key]
                  )?.name_code)
                );
              } else if (userColumns.includes(key)) {
                columnTableCenter(
                  (data[key] = (props.ImagingOrderInterfaceSequence?.userOptions || []).find(
                    (user: any) => user?.id === item[key]
                  )?.full_name_code)
                );
              } else if (dateTimeColumns.includes(key)) {
                data[key] = columnTableCenter(item[key] ? dateToStringBE(item[key]) : item[key]);
              } else if (conditionColumns.includes(key)) {
                data[key] = columnTableCenter(item[key] ? "Yes" : "No");
              } else if (key === "emr") {
                data[key] = columnTableCenter(formatEmr(encounterData, item[key]));
              } else if (key === "order_summary_detail_cache") {
                const cache = { ...item[key] };

                if (cache?.order_summary_detail) {
                  cache.order_summary_detail = cache.order_summary_detail.replace(/<br\s*\/?>/gi, ", ");
                }

                data[key] = JSON.stringify(cache, null, 2);
              } else if (key === "order_summary") {
                data[key] = item[key];
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            },
            {}
          ),
        };
      }
    );
  }, [
    selectedImagingOrder,
    props.ImagingOrderInterfaceSequence?.ImagingOrderInterfaceList,
    tableHeaders,
    PaymentStatusOptions,
    OrderTypeOptions,
    OrderStatusOptions,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardImagingOrderInterfaceUX
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchImagingOrder={props.ImagingOrderInterfaceSequence?.searchImagingOrder}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedImagingOrder.length ===
                    (props.ImagingOrderInterfaceSequence?.ImagingOrderInterfaceList || []).length &&
                  (props.ImagingOrderInterfaceSequence?.ImagingOrderInterfaceList || []).length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        imagingOrderInterfaceList={imagingOrderInterfaceList}
        paginatedItems={props.ImagingOrderInterfaceSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title={intl.formatMessage({ id: "ค้นหา" })}
            name="SEARCH"
            size="medium"
          />
        }
        triggerButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("TRIGGER")}
            disabled={handleDisabledTrigger}
            // data
            paramKey={`${CARD_NAME}_TRIGGER`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_TRIGGER`]}
            // config
            color="blue"
            title="Trigger"
            name="TRIGGER"
            size="medium"
          />
        }
      />
    </>
  );
};

CardImagingOrderInterface.displayName = "CardImagingOrderInterface";

export default React.memo(CardImagingOrderInterface);

const dateTimeColumns: Partial<keyof ImagingOrderInterfaceSerializer>[] = [
  "order_time",
  "order_ack_time",
  "doctor_edit_time",
  "order_approve_time",
  "order_perform_time",
  "order_receive_time",
  "created",
  "edited",
];

const conditionColumns: Partial<keyof ImagingOrderInterfaceSerializer>[] = [
  "is_advance",
  "is_emergency",
  "is_out_perform_div",
  "is_seen",
];

const divisionColumns: Partial<keyof ImagingOrderInterfaceSerializer>[] = [
  "order_div",
  "order_ack_div",
  "out_perform_div",
  "order_perform_div",
];

const userColumns: Partial<keyof ImagingOrderInterfaceSerializer | keyof OrderItemsHeader>[] = [
  "order_ack_by",
  "order_perform_by",
  "order_receive_by",
  "order_items_0_edit_user",
];

const doctorColumns: Partial<keyof ImagingOrderInterfaceSerializer>[] = [
  "order_by",
  "approved_by",
  "edited_doctor",
];

const columnWidths: WidthsType = {
  id: 70,
  created: 150,
  edited: 150,
  order_time: 150,
  order_ack_time: 150,
  doctor_edit_time: 150,
  order_approve_time: 150,
  order_perform_time: 150,
  order_receive_time: 150,
  order_by: 200,
  approved_by: 200,
  order_ack_by: 200,
  order_perform_by: 200,
  order_receive_by: 200,
  order_summary: 200,
  care_provider: 200,
  emr: 400,
  order_type: 200,
  order_status: 150,
  order_items_0_edit_user: 200,
  order_items_0_created: 150,
  order_items_0_edited: 150,
  order_summary_detail_cache: 250,
};

const convertToRow = (item: ImagingOrderInterfaceSerializer) => {
  const firstOrderItem = item.order_items?.[0] || {};

  return {
    ...item,
    order_items_0_edit_user: firstOrderItem?.edit_user,
    order_items_0_created: firstOrderItem?.created,
    order_items_0_edited: firstOrderItem?.edited,
  };
};

const tableHeaders = GenerateTableHeader(columnWidths);
